import { computed, inject, Injectable, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import { map, timeout } from 'rxjs/operators';

import { useToaster } from 'app/shared/composables/use-toaster';
import { DEFAULT_LOCALE } from 'app/shared/i18n/i18n.config';
import { User } from 'app/shared/models/User';
import { getContactPageUrl } from 'app/utils/url';

import { UserService } from './user.service';

const LOAD_TIMEOUT_MS = 30 * 1000;
const SIGN_OUT_TIMEOUT_MS = 3 * 1000;

export function contactPageUrlFor(user: User | null): string | null {
    return user?.country ? getContactPageUrl(user.country) : null;
}

export function requireInstaller(user: User | null): User {
    if (!user) throw new Error('No signed-in installer');
    return user;
}

/**
 * Owns the installer session: the signed-in installer and the initial load that fetches it.
 * The language stays owned by ngx-translate; the session only exposes it as a signal.
 */
@Injectable({ providedIn: 'root' })
export class SessionStore {
    private readonly userService = inject(UserService);
    private readonly translate = inject(TranslateService);
    private readonly toaster = useToaster();

    private readonly currentUser = signal<User | null>(null);
    private readonly loadingState = signal(false);
    private readonly signOutTasks: (() => Promise<unknown>)[] = [];

    readonly user = this.currentUser.asReadonly();
    readonly loading = this.loadingState.asReadonly();
    readonly language = toSignal(this.translate.onLangChange.pipe(map(({ lang }) => lang)), {
        initialValue: this.translate.getCurrentLang() || DEFAULT_LOCALE,
    });
    readonly contactPageUrl = computed(() => contactPageUrlFor(this.currentUser()));

    /** Resolves once the installer is loaded or the failure is reported; never rejects. */
    async load(): Promise<void> {
        this.loadingState.set(true);

        try {
            this.currentUser.set(await firstValueFrom(this.userService.getInstaller().pipe(timeout(LOAD_TIMEOUT_MS))));
            this.loadingState.set(false);
        } catch (error) {
            // A failed load keeps `loading` set, as the NgRx session did.
            console.error(error);
            this.toaster.error({
                title: this.translate.instant('ERROR_SITES_TAKES_TO_LONG_TO_LOAD_OR_UNAVAILABLE'),
                duration: LOAD_TIMEOUT_MS,
            });
        }
    }

    /** For commands that only exist inside a signed-in session: throws outside one. */
    signedInInstaller(): User {
        return requireInstaller(this.currentUser());
    }

    /** Work to finish before the installer signs out, such as ending a video call. */
    beforeSignOut(task: () => Promise<unknown>): void {
        this.signOutTasks.push(task);
    }

    /** Runs the sign-out tasks. Resolves once they settle, or after a short timeout so logging out cannot hang. */
    async signOut(): Promise<void> {
        let timer: ReturnType<typeof setTimeout> | undefined;
        const timeout = new Promise<void>((resolve) => {
            timer = setTimeout(resolve, SIGN_OUT_TIMEOUT_MS);
        });

        await Promise.race([Promise.allSettled(this.signOutTasks.map(async (task) => task())), timeout]);
        clearTimeout(timer);
    }

    clear(): void {
        this.currentUser.set(null);
        this.loadingState.set(false);
    }
}
